import React, { FC, useState } from "react";

import { useTranslation } from "lib/i18n/use-translation";

import {
  CompletedOn,
  CompletionError,
  MechanicProps,
  useCompletion,
} from "./shared";
import styles from "../../december-doors.module.scss";

const MAX_ANSWER_LENGTH = 2000;

interface WriteMechanicProps extends MechanicProps {
  questions: string[];
  answers: string[] | null;
}

const sameAnswers = (a: string[], b: string[]) =>
  a.every((answer, index) => answer.trim() === b[index].trim());

const WriteMechanic: FC<WriteMechanicProps> = ({
  door,
  questions,
  answers,
  onComplete,
}) => {
  const { t } = useTranslation(["decemberDoors"]);
  const { submit, isSaving, hasFailed } = useCompletion(onComplete);
  const savedAnswers = answers ?? questions.map(() => "");
  const [drafts, setDrafts] = useState<string[]>(savedAnswers);

  const isBlank = drafts.every((draft) => draft.trim() === "");
  const isSaved = door.isCompleted && sameAnswers(drafts, savedAnswers);

  const setDraft = (index: number, value: string) =>
    setDrafts((current) =>
      current.map((draft, at) => (at === index ? value : draft))
    );

  return (
    <form
      className={styles.mechanic}
      onSubmit={(event) => {
        event.preventDefault();
        submit({ answers: drafts });
      }}
    >
      {questions.map((question, index) => {
        const id = `door-${door.id}-answer-${index}`;
        return (
          <div key={id} className={styles["mechanic-field"]}>
            <label htmlFor={id} className={styles["mechanic-label"]}>
              {question}
            </label>
            <textarea
              id={id}
              className={styles["mechanic-textarea"]}
              placeholder={t("decemberDoors:mechanic.write.placeholder")}
              value={drafts[index]}
              maxLength={MAX_ANSWER_LENGTH}
              rows={3}
              onChange={(event) => setDraft(index, event.target.value)}
            />
          </div>
        );
      })}
      <div className={styles["mechanic-row"]}>
        <button
          type="submit"
          className={styles["mechanic-primary"]}
          disabled={isBlank || isSaved || isSaving}
        >
          {t("decemberDoors:mechanic.write.save")}
        </button>
        {isSaved && (
          <span className={styles["mechanic-status"]} role="status">
            {t("decemberDoors:mechanic.write.saved")}
          </span>
        )}
      </div>
      <CompletionError show={hasFailed} />
      <CompletedOn door={door} />
    </form>
  );
};

export default WriteMechanic;
